import React, { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown, Send, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { toast } from "sonner";

/**
 * ReviewForm — inline mini-form to ask "Did this AI work?"
 * Used inside SearchResults cards.
 */
export const ReviewForm = ({ toolName, originalPrompt }) => {
  const [voted, setVoted] = useState(null); // null | true | false
  const [text, setText] = useState("");
  const [showText, setShowText] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleVote = async (didWork) => {
    setVoted(didWork);
    setShowText(true);
  };

  const handleSubmit = async () => {
    if (voted === null) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tool_name: toolName, did_work: voted, text, original_prompt: originalPrompt || "" }),
      });
      if (res.ok) {
        toast.success("Thanks for your feedback!");
        setSubmitted(true);
      } else {
        const err = await res.json();
        if (res.status === 401) toast.error("Sign in to leave a review");
        else toast.error(err.detail || "Failed to submit");
      }
    } catch (e) { toast.error("Something went wrong"); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="text-[10px] text-neon flex items-center gap-1 mt-2" data-testid={`review-thanks-${toolName}`}>
        <Star className="w-3 h-3" /> Thanks for rating {toolName}!
      </div>
    );
  }

  return (
    <div className="mt-2 pt-2 border-t border-white/5" data-testid={`review-form-${toolName}`}>
      {!showText ? (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/40">Did this AI work for you?</span>
          <button onClick={() => handleVote(true)} className="text-[10px] flex items-center gap-1 text-white/50 hover:text-neon transition-colors" data-testid={`review-yes-${toolName}`}>
            <ThumbsUp className="w-3 h-3" /> Yes
          </button>
          <button onClick={() => handleVote(false)} className="text-[10px] flex items-center gap-1 text-white/50 hover:text-red-400 transition-colors" data-testid={`review-no-${toolName}`}>
            <ThumbsDown className="w-3 h-3" /> No
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1 text-[10px]">
            {voted ? <ThumbsUp className="w-3 h-3 text-neon" /> : <ThumbsDown className="w-3 h-3 text-red-400" />}
            <span className={voted ? "text-neon" : "text-red-400"}>{voted ? "It worked!" : "Didn't work"}</span>
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Tell us more (optional)…" className="bg-dark-subtle border-white/10 text-xs min-h-[40px]" maxLength={500} data-testid={`review-text-${toolName}`} />
          <Button onClick={handleSubmit} disabled={submitting} size="sm" className="bg-purple-700 text-white hover:bg-purple-600 text-[10px] h-7" data-testid={`review-submit-${toolName}`}>
            <Send className="w-3 h-3 mr-1" /> {submitting ? "..." : "Submit"}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * RecentReviews — public component that lists recent positive reviews + stats.
 * Used on Landing page and Results page.
 */
export const RecentReviews = ({ limit = 3, compact = false }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rRes, sRes] = await Promise.all([
          fetch(`${API}/reviews/recent?limit=${limit}`),
          fetch(`${API}/reviews/stats`),
        ]);
        if (rRes.ok) setReviews(await rRes.json());
        if (sRes.ok) setStats(await sRes.json());
      } catch (e) {}
    })();
  }, [limit]);

  if (!stats || stats.total_reviews === 0) return null;

  return (
    <div className={compact ? "" : "py-12"} data-testid="recent-reviews">
      {!compact && (
        <div className="mb-6 text-center">
          <h3 className="font-inter font-bold text-2xl mb-1">What users say</h3>
          <p className="text-white/40 text-sm">
            <span className="text-neon font-bold">{stats.success_percentage}%</span> success rate · {stats.total_reviews.toLocaleString()} reviews
          </p>
        </div>
      )}
      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-3"} gap-3`}>
        {reviews.map((r) => (
          <div key={r.review_id} className="bg-dark-paper border border-white/10 rounded-xl p-4" data-testid={`review-card-${r.review_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <ThumbsUp className="w-3.5 h-3.5 text-neon" />
              <span className="text-xs font-semibold text-purple-300">{r.tool_name}</span>
            </div>
            <p className="text-white/60 text-xs leading-relaxed">&ldquo;{r.text}&rdquo;</p>
            <p className="text-[10px] text-white/30 mt-2">— {r.user_name}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
